import {useCallback, useRef, useState} from "react";
import type {Moment} from "moment";
import {getLocationAvailabilityCalendar} from "../../../components/location/requests/location-requests";
import {getErrorMessageForStandardResponse} from "../../../util/NetworkErrorUtil";
import {useFeatureFlags} from "../../routing/useFeatureFlags";
import type {AvailabilityByDate} from "../../../components/location/profile/AvailabilityPickersDay";

export interface LocationAvailabilityCalendarState {
  /** Days loaded so far, keyed by YYYY-MM-DD; null when the calendar shouldn't show availability. */
  availability: AvailabilityByDate | null;
  /** Loads a month's availability once; call it for every month a calendar shows. */
  loadMonth: (month: Moment) => void;
}

interface LoadedAvailability {
  locationId: string;
  enforcesCapacity: boolean | null;
  days: AvailabilityByDate;
}

const ISO_DAY = 'YYYY-MM-DD';

/**
 * Per-day availability for a location's booking calendar (#3271). Only GMS PRO locations are held to
 * capacity, so the API reports when a location isn't and no more months are fetched for it.
 */
const useLocationAvailabilityCalendar = (locationId?: string | null): LocationAvailabilityCalendarState => {
  const {supplierCapacitySchedule} = useFeatureFlags();
  const [loaded, setLoaded] = useState<LoadedAvailability | null>(null);
  const requestedMonths = useRef(new Set<string>());
  const currentLocationId = useRef(locationId);
  if (currentLocationId.current !== locationId) {
    // Only the current location's days are kept, so returning to an earlier location must refetch its months.
    requestedMonths.current.clear();
    currentLocationId.current = locationId;
  }
  const enforcesCapacity = loaded && loaded.locationId === locationId ? loaded.enforcesCapacity : null;

  const loadMonth = useCallback((month: Moment) => {
    if (!supplierCapacitySchedule || !locationId || enforcesCapacity === false || !month?.isValid()) return;
    const requestKey = `${locationId}|${month.format('YYYY-MM')}`;
    if (requestedMonths.current.has(requestKey)) return;
    requestedMonths.current.add(requestKey);

    getLocationAvailabilityCalendar(
      locationId,
      month.clone().startOf('month').format(ISO_DAY),
      month.clone().endOf('month').format(ISO_DAY),
    ).then(({body}) => {
      if (currentLocationId.current !== locationId) {
        requestedMonths.current.delete(requestKey);
        return;
      }
      setLoaded((previous) => {
        const days = previous?.locationId === locationId ? {...previous.days} : {};
        body.days.forEach((day) => {
          days[day.date] = day;
        });
        return {locationId, enforcesCapacity: body.enforcesCapacity, days};
      });
    }).catch((error) => {
      requestedMonths.current.delete(requestKey);
      console.error(getErrorMessageForStandardResponse(error, 'Failed to load location availability'));
    });
  }, [locationId, supplierCapacitySchedule, enforcesCapacity]);

  const showAvailability = supplierCapacitySchedule && enforcesCapacity === true;
  return {availability: showAvailability && loaded ? loaded.days : null, loadMonth};
};

export default useLocationAvailabilityCalendar;
